/**
 * Hook d'autorisations métier (source de vérité API).
 * Responsabilité: exposer des booléens simples pour l'UI (menus, guards, actions),
 * sans relire le token ni recopier la logique dans chaque composant.
 */

import { useContext, useMemo } from 'react'
import { UserContext } from 'core/contexts/UserContext'

export interface MyPermissions {
  /** La session métier est chargée et fiable. */
  isBusinessReady: boolean
  /** Superadmin métier (API: GET /utilisateurs/me.superAdmin). */
  isSuperAdmin: boolean
  /** Admin sur au moins un tag du client courant (API: permission 'administrateur'). */
  isAdminForCurrentClient: boolean

  /** Accès à l'écran Dashboard (logique historique: superadmin OU au moins 1 tag). */
  canAccessDashboard: boolean
  /** Accès à la gestion des profils (historique: admin OU superadmin). */
  canManageProfiles: boolean
  /** Accès à l'admin des tags (superadmin). */
  canManageTagsAdmin: boolean
}

export function useMyPermissions(): MyPermissions {
  const { businessStatus, user, isAdmin, tagsMe } = useContext(UserContext)

  return useMemo(() => {
    const isBusinessReady = businessStatus === 'ready'
    const isSuperAdmin = Boolean(user?.superAdmin)
    const isAdminForCurrentClient = isAdmin

    const canAccessDashboard =
      isBusinessReady && (isSuperAdmin || tagsMe.length > 0)

    const canManageProfiles = isBusinessReady && (isSuperAdmin || isAdmin)
    const canManageTagsAdmin = isBusinessReady && isSuperAdmin

    return {
      isBusinessReady,
      isSuperAdmin,
      isAdminForCurrentClient,
      canAccessDashboard,
      canManageProfiles,
      canManageTagsAdmin,
    }
  }, [businessStatus, user?.superAdmin, isAdmin, tagsMe.length])
}
